<template>
	<template v-if="data">
		<ButtonSticky v-if="userShouldMakeDecision" @buttonclick="makeDecision()" :width="200">
			<template v-slot:icon><decisionIcon /></template>
			<template v-slot:text> Entscheidung treffen </template>
		</ButtonSticky>

		<section>
			<ButtonBack />

			<el-row class="titlerow">
				<el-col :sm="12">
					<h1>{{ data.title }}</h1>
				</el-col>
				<el-col :sm="12">
					<div :class="`has-status has-status--${$getStateColor('case', data.state)}`">
						{{ $getStateFullLabel('case', data.state, false) }}
					</div>
				</el-col>
			</el-row>
		</section>

		<section v-if="userDecision">
			<TheBox :title="'Meine Entscheidung'">
				<DetailsUserDecision :decisionCategories="userDecision.categories" :decisionState="userDecision.decisionState" />
				<p v-if="!decisionDateStillValid">Die Frist für die Entscheidung ist bereits abgelaufen.</p>
			</TheBox>
		</section>

		<SectionDecision
			v-if="
				data.state === CaseState.ExaminationSmallSenate ||
				data.state === CaseState.ExaminationSmallSenateRunning ||
				data.state === CaseState.Statements
			"
			:headline="'Entscheidungen kleiner Senat'"
			:data="smallSenateDecisions"
			:caseTitle="data.title"
			:preVoteEndDate="data.preVoteEndDate ? data.preVoteEndDate : ''"
			:voteEndDate="data.voteEndDate ? data.voteEndDate : ''"
		/>

		<SectionStatistics
			v-if="userIsSmallSenate && showSmallSenateStatistic(data)"
			:statistics="statistic"
			:type="DecisionType.SmallSenate"
			:title="data.preVoteRole?.name"
			:werberatRoles="null"
			:caseHid="data.hid"
		/>

		<section>
			<TheBox
				v-if="hasDecision(data)"
				:title="isSmallSenateDecision(data) ? 'Senatsentscheidung' : 'Endgültige Entscheidung:'"
				:id="'decision-final'"
			>
				<div :class="`has-status has-status--${$getStateColor('case', data.state)}`">
					{{ $getStateFullLabel('case', data.state, false) }}
				</div>

				<h3>Begründung</h3>
				<div class="text">{{ data.reasonText ? data.reasonText : '-' }}</div>

				<el-row v-if="data.decidedDate" class="decided-info">
					<el-text>Entscheidung getroffen: {{ $formatDate(data.decidedDate, 'HH:MM, DD.MM.YYYY') }}</el-text>
				</el-row>
			</TheBox>
		</section>

		<SectionStatistics
			v-if="userIsJungerWerberat && showWerberatStatistic(data) && data.publishedDate"
			:statistics="statistic"
			:type="DecisionType.JungerWerberat"
			:werberatRoles="data.voteRoles"
			:caseHid="data.hid"
		/>

		<SectionDecision
			v-if="werberatDecisions.length > 0 && hasDecision(data)"
			:headline="'Werberatsentscheidungen mit Kommentar'"
			:data="werberatDecisions"
			:caseTitle="data.title"
			:preVoteEndDate="data.preVoteEndDate ? data.preVoteEndDate : ''"
			:voteEndDate="data.voteEndDate ? data.voteEndDate : ''"
		/>

		<section v-if="data.externalNote">
			<TheBox :title="'Hinweis (auch für Werberäte sichtbar)'">
				<div class="text">{{ data.externalNote }}</div>
			</TheBox>
		</section>

		<SectionBeschwerden :complaints="data.complaints ? data.complaints : []" @loadNextPage="null" />

		<SectionStatements :statements="data.statements ? data.statements : []" />

		<DetailsMedia
			v-if="caseFiles.length > 0"
			:files="caseFiles"
			@addToFiles="null"
			@removeFromFiles="null"
			:showAddButton="false"
			:pageType="'fall'"
		/>
		<DetailsLinks
			v-if="caseLinks.length > 0"
			:links="caseLinks"
			@addToLinks="null"
			@removeFromLinks="null"
			:showAddButton="false"
			:pageType="'fall'"
		/>

		<section>
			<TheBox :title="'Fall Infos'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Falltitel:" prop="title">
							<span>{{ data.title }}</span>
						</el-form-item>

						<el-form-item label="Beschwerdegrund:" prop="category">
							<span>{{ data?.category.name }}</span>
						</el-form-item>

						<el-form-item label="Medium:" prop="mediums">
							<span>{{ data?.mediums.map((medium) => medium.name).join(', ') }}</span>
						</el-form-item>

						<el-form-item label="Reichweite:" prop="mediums">
							<span>{{ data?.reaches.length > 0 ? data?.reaches.map((medium) => medium.name).join(', ') : '-' }}</span>
						</el-form-item>

						<el-form-item label="Datum:">
							<span>{{ $formatDate(data.createdDate) }}</span>
						</el-form-item>

						<el-form-item label="ID:">
							<span>{{ data.hid }}</span>
						</el-form-item>

						<el-form-item label="Rückfragen:">
							<span>{{ getCallbacksCount(data) }}</span>
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Beschwerden:">
							<span>{{ data.complaints?.length }}</span>
						</el-form-item>

						<el-form-item label="Stn-Gruppen:">
							{{ data.voteRoles && data.voteRoles.length > 0 ? data.voteRoles?.map((role) => role.name).join(', ') : '-' }}
						</el-form-item>

						<el-form-item label="Stn. bis:">
							<span>{{ getCaseVoteEnde(data, $formatDate) }}</span>
						</el-form-item>

						<el-form-item label="Stn. angefragt:">
							<span>{{ data.statements?.filter((st) => st.answer).length }}</span>
						</el-form-item>

						<el-form-item label="WR-Ent. bis:">
							<span>{{ data.voteEndDate ? $formatDate(data.voteEndDate) : '-' }}</span>
						</el-form-item>

						<el-form-item label="Auf Website veröffentlicht:">
							<span>{{ data.publishedDate ? 'Ja' : 'Nein' }}</span>
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>
		</section>
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import DialogRequestStatement from '@/components/dialogs/DialogRequestStatement.vue';
import { getCase, getCaseStatistics } from '@/requests/cases';
import { SaveCaseRequest } from '@/types/formdataTypes';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { Delete, Plus } from '@element-plus/icons-vue';
import { defineComponent, computed } from 'vue';
import decisionIcon from '../assets/icons/decisionIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';
import DecisionsTable from '../components/tables/DecisionsTable.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { CaseResponse, CaseState, DecisionType, DetailFile, DetailLink, SendCaseType, StatisticResponse } from '../types/types';
import { getCaseFiles } from '../utils/fileHandling/files';
import { getCaseLinks } from '../utils/linkHandling/links';
import { texts } from '../utils/texts.js';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import DetailsUserDecision from './sections/DetailsUserDecision.vue';
import { useStore, authUser } from '../store';
import { Swiper, SwiperSlide } from 'swiper/vue';
import SectionDecision from './sections/SectionDecision.vue';
import SectionBeschwerden from './sections/SectionBeschwerden.vue';
import SectionStatements from './sections/SectionStatements.vue';
import SectionStatistics from './sections/SectionStatistics.vue';
import { checkIfDateInPast } from '@/utils/search/searchUtils';

import {
	getCallbacksCount,
	getCaseVoteEnde,
	isSmallSenateDecision,
	hasDecision,
	isSentToSmallSenate,
	isInStatements,
	isSentToWerberat,
} from '@/utils/casesHandling/cases';

import { showSmallSenateStatistic, showWerberatStatistic } from '@/utils/statisticHandling/statistics';

export default defineComponent({
	name: 'FallDetailWerberat',
	data() {
		return {
			data: null as CaseResponse | null,
			saveCaseRequest: null as SaveCaseRequest | null,
			loading: true,
			SendCaseType,
			CaseState,
			caseFiles: [] as DetailFile[],
			caseLinks: [] as DetailLink[],
			DecisionType,
			statistic: null as StatisticResponse | null,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	setup() {
		const store = useStore();
		const isMobile = computed(() => store.state.isMobile);
		const userRoles = authUser.value.roles ? authUser.value.roles.map((role) => role.name) : [];

		return {
			store,
			isMobile,
			user: authUser,
			userRoles,
		};
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		DecisionsTable,
		Plus,
		Delete,
		RoleSlider,
		DetailsMedia,
		DetailsLinks,
		decisionIcon,
		DialogRequestStatement,
		DetailsUserDecision,
		Swiper,
		SwiperSlide,
		SectionDecision,
		SectionBeschwerden,
		SectionStatements,
		SectionStatistics,
	},
	computed: {
		texts() {
			return texts;
		},
		smallSenateDecisions() {
			if (!this.data) return [];

			return this.data.decisions.filter((d) => d.decisionType === DecisionType.SmallSenate).filter((d) => d !== this.userDecision);
		},
		werberatDecisions() {
			if (!this.data) return [];

			return this.data.decisions
				.filter((d) => d.decidedDate && (d.decisionType === DecisionType.Werberat || d.decisionType === DecisionType.JungerWerberat) && d.reasonText)
				.filter((d) => d !== this.userDecision);
		},
		userDecision() {
			if (!this.data || !this.store.state.user) return null;
			const allMyDecisions = this.data.decisions.filter((d) => d.user.id === this.store.state.user!.id);

			if (this.data.state === CaseState.ExaminationSmallSenate || this.data.state === CaseState.ExaminationSmallSenateRunning) {
				return allMyDecisions.find((d) => d.decisionType === DecisionType.SmallSenate);
			} else {
				return allMyDecisions.find((d) => d.decisionType !== DecisionType.SmallSenate);
			}
		},
		decisionDateStillValid() {
			if (!this.data) return false;

			// if state = smallsenate then check if preVoteDate is valid
			if (this.data.state === CaseState.ExaminationSmallSenate || this.data.state === CaseState.ExaminationSmallSenateRunning) {
				let date = this.data.preVoteEndDate;
				if (!date || checkIfDateInPast(new Date(date))) return false;
			}

			// if state = werberat then check if voteDate is valid
			// or state == NoStatus (after decision has been made autommatically NoStatus)
			if (
				this.data.state === CaseState.ExaminationWerberat ||
				this.data.state === CaseState.ExaminationWerberatRunning ||
				this.data.state === CaseState.NoStatus
			) {
				let date = this.data.voteEndDate;
				if (!date || checkIfDateInPast(new Date(date))) return false;
			}

			return true;
		},
		userShouldMakeDecision() {
			if (!this.data || !this.store.state.user) return false;
			if (!this.userDecision) return false;

			if (!this.decisionDateStillValid) return false;

			return (
				this.data.state === CaseState.ExaminationWerberat ||
				this.data.state === CaseState.ExaminationWerberatRunning ||
				this.data.state === CaseState.ExaminationSmallSenate ||
				this.data.state === CaseState.ExaminationSmallSenateRunning
			);
		},
		userIsSmallSenate() {
			return this.userRoles.includes('Kleiner Senat A') || this.userRoles.includes('Kleiner Senat B');
		},
		userIsJungerWerberat() {
			return this.userRoles.includes('Junger Werberat');
		},
	},
	methods: {
		makeDecision() {
			if (!this.data) return;
			this.$router.push({ name: 'EntscheidungEdit', params: { id: String(this.userDecision!.id) } });
		},
		async loadCase() {
			const loading = startLoading('#fall');

			const result = await getCase(this.id);
			if (result.success) {
				this.data = result.data;

				this.caseFiles = getCaseFiles(this.data);
				this.caseLinks = getCaseLinks(this.data);
			}

			this.loading = false;
			stopLoading(loading);
		},
		async loadStatistics() {
			const result = await getCaseStatistics(this.id);
			if (result.success) {
				this.statistic = result.data;
			}
		},
		getCallbacksCount,
		getCaseVoteEnde,
		isSmallSenateDecision,
		hasDecision,
		isSentToSmallSenate,
		isInStatements,
		isSentToWerberat,
		showSmallSenateStatistic,
		showWerberatStatistic,
	},
	mounted() {
		this.loadCase();

		this.loadStatistics();
	},
});
</script>
